/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { Logger } from '../utils/logger';
import type { XHubChatError } from './error';

export type Body = Record<string, any> | BodyInit;

export type QueryDict = Record<string, string[] | string | number | boolean | undefined>;

/**
 * @experimental
 * Unencrypted access and (optional) refresh token
 */
export type AccessTokens = {
  /**
   * The new access token to use for authenticated requests
   */
  accessToken: string;
  /**
   * The new refresh token to use for refreshing tokens, optional
   */
  refreshToken?: string;
  /**
   * Approximate date when the access token will expire, optional
   */
  expiry?: Date;
};

/**
 * @experimental
 * Function that performs token refresh using the given refreshToken.
 * Returns a promise that resolves to the refreshed access and (optional) refresh tokens.
 *
 * Can be passed to HttpApi instance as {@link IHttpOpts.tokenRefreshFunction} during client creation {@link ICreateClientOpts}
 */
export type TokenRefreshFunction = (refreshToken: string) => Promise<AccessTokens>;

/** Options object for `FetchHttpApi` and {@link XHubChatHttpApi}. */
export type IHttpOpts = {
  fetchFn?: typeof globalThis.fetch;

  baseUrl: string;
  idBaseUrl?: string;
  prefix: string;

  accessToken?: string;
  /**
   * Used in conjunction with tokenRefreshFunction to attempt token refresh
   */
  refreshToken?: string;
  /**
   * Function to attempt token refresh when a possibly expired token is encountered
   * Optional, only called when a refreshToken is present
   */
  tokenRefreshFunction?: TokenRefreshFunction;
  useAuthorizationHeader?: boolean; // defaults to true

  /** For historical reasons, must be set to `true`. Will eventually be removed. */
  onlyData?: boolean;

  localTimeoutMs?: number;

  /** Optional logger instance. If provided, requests and responses will be logged. */
  logger?: Logger;

  /**
   * This requires param to server can identify the client application.
   * @example { api_key: $API_KEY }
   */
  clientParams?: Record<string, string>;
};

export enum HttpApiEvent {
  SessionLoggedOut = 'Session.logged_out',
  NoConsent = 'no_consent',
  TokenExpired = 'token.expired',
}

export type HttpApiEventHandlerMap = {
  /**
   * Fires whenever the login session the JS SDK is using is no
   * longer valid and the user must log in again.
   * NB. This only fires when action is required from the user, not
   * when then login session can be renewed by using a refresh token.
   * @example
   * ```
   * matrixClient.on("Session.logged_out", function(errorObj){
   *   // show the login screen
   * });
   * ```
   */
  [HttpApiEvent.SessionLoggedOut]: (err: XHubChatError) => void;
  /**
   * Fires when the JS SDK receives a M_CONSENT_NOT_GIVEN error in response
   * to a HTTP request.
   * @example
   * ```
   * matrixClient.on("no_consent", function(message, contentUri) {
   *     console.info(message + ' Go to ' + contentUri);
   * });
   * ```
   */
  [HttpApiEvent.NoConsent]: (message: string, consentUri: string) => void;
  /**
   * Fires when an access token has expired and needs to be refreshed.
   * The application should refresh the token and call setAccessToken with the new token.
   * @example
   * ```
   * client.on("token.expired", function() {
   *   // Refresh token via your auth service
   *   const newToken = await authService.refreshToken();
   *   client.setAccessToken(newToken);
   * });
   * ```
   */
  [HttpApiEvent.TokenExpired]: () => void;
};

/** Options object for `FetchHttpApi.requestOtherUrl`. */
export type BaseRequestOpts = {
  /**
   * map of additional request headers
   */
  headers?: Record<string, string>;
  abortSignal?: AbortSignal;
  /**
   * The maximum amount of time to wait before
   * timing out the request. If not specified, there is no timeout.
   */
  localTimeoutMs?: number;
  keepAlive?: boolean; // defaults to false

  /**
   * By default, we will:
   *
   *  If the `body` is an object, JSON-encode it and set `Content-Type: application/json` in the
   *     request headers (unless overridden by {@link headers}).
   *
   *  Set `Accept: application/json` in the request headers (again, unless overridden by {@link headers}).
   *
   *  Parse the response as JSON and return the parsed response.
   *
   * Setting this to `false` inhibits all three behaviors, and the response is instead parsed as a UTF-8 string. It
   * defaults to `true`, unless {@link rawResponseBody} is set.
   *
   * @deprecated Instead of setting this to `false`, set {@link rawResponseBody} to `true`.
   */
  json?: boolean;

  /**
   * Setting this to `true` does two things:
   *
   *  Inhibits the automatic addition of `Accept: application/json` in the request headers.
   *
   *  Causes the raw response to be returned as a {@link https://developer.mozilla.org/en-US/docs/Web/API/Blob|Blob}
   *    instead of parsing it as JSON.
   */
  rawResponseBody?: boolean;
} & Pick<RequestInit, 'priority'>;

export type UploadProgress = {
  loaded: number;
  total: number;
};

export type IRequestOpts = {
  /**
   * The alternative base url to use.
   * If not specified, uses this.opts.baseUrl
   */
  baseUrl?: string;
  /**
   * The full prefix to use e.g.
   * "/_matrix/client/v2_alpha". If not specified, uses this.opts.prefix.
   */
  prefix?: string;

  // Set to true to prevent the request function from emitting a Session.logged_out event.
  // This is intended for use on endpoints where M_UNKNOWN_TOKEN is a valid/notable error response,
  // such as with token refreshes.
  inhibitLogoutEmit?: boolean;
} & BaseRequestOpts;

export type Upload = {
  loaded: number;
  total: number;
  promise: Promise<UploadResponse>;
  abortController: AbortController;
};

export type UploadResponse = {
  content_uri: string;
};

export type FileType = XMLHttpRequestBodyInit;

export type UploadOpts = {
  /**
   * Name to give the file on the server. Defaults to <tt>file.name</tt>.
   */
  name?: string;
  /**
   * Content-type for the upload. Defaults to
   *   <tt>file.type</tt>, or <tt>application/octet-stream</tt>.
   */
  type?: string;
  /**
   * if false will not send the filename,
   *   e.g for encrypted file uploads where filename leaks are undesirable.
   *   Defaults to true.
   */
  includeFilename?: boolean;
  /**
   * Optional. Called when a chunk of
   *    data has been uploaded, with an object containing the fields `loaded`
   *    (number of bytes transferred) and `total` (total size, if known).
   */
  progressHandler?: (progress: UploadProgress) => void;
  abortController?: AbortController;
};

export type IContentUri = {
  base: string;
  path: string;
  params: {
    access_token: string;
  };
};

export type ResponseCursor<T, K extends string = 'data'> = {
  [key in K]: T;
} & {
  next_cursor: string | null;
  has_next: boolean;
  total_count?: number;
};

export type CursorParams = {
  limit?: number;
  cursor?: string;
};

export type HttpResponse<T> = {
  code: number;
  message: string;
  success: boolean;
  data: T;
};
